import type { PayGateBannerProps } from './Banner';
import type { ReactNode } from 'react';

import { createContext, useContext } from 'react';

import useClassy from '@core/hooks/useClassy';
import Flex from '@ui/Flex';

import PayGateBanner from './Banner';
import classes from './index.module.scss';

/**
 * Signals that an ancestor `PayGate` is already rendering a locked overlay.
 * Nested `PayGate`s short-circuit when this is true so that only one banner
 * is shown to the user. The outermost locked gate wins
 */
const OuterLockContext = createContext(false);

export interface PayGateProps {
  /**
   * Whether the children content is locked or open.
   * - `locked` - User does not have access to the feature and will be prompted to upgrade.
   * - `open` - User has access to the feature and will see the children content.
   */
  access: 'locked' | 'open';
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  /**
   * The feature that is gated. Used to determine the content to display in the
   * overlay. Omit this prop to lock the feature without displaying a banner.
   */
  feature?: PayGateBannerProps['feature'];
  kind?: 'disabled' | 'overlay';
}

/**
 * Used to gates a feature's UI if the user doesn not have the correct plan
 * permissions. When a feature is gated it displays an overlay with a message
 * and a button to upgrade the plan, and renders child content as inert in the
 * background.
 */
export default function PayGate({
  access,
  children,
  className,
  contentClassName,
  feature,
  kind = 'overlay',
}: PayGateProps) {
  const bem = useClassy(classes, 'PayGate');
  const isInsideOuterLock = useContext(OuterLockContext);

  // An ancestor `PayGate` is already showing a locked overlay; render children unwrapped so the user only sees one banner.
  if (isInsideOuterLock) return <>{children}</>;

  if (access === 'open') return <>{children}</>;

  const content =
    kind === 'disabled' ? (
      <div className={bem('&', '&_disabled', className)}>{children}</div>
    ) : (
      <div className={bem('&', className)}>
        <div className={bem('-content', contentClassName)} inert="">
          {children}
        </div>
        <Flex align="center" className={bem('-overlay', !feature && '-overlay_disabled')} justify="center">
          {!!feature && <PayGateBanner feature={feature} />}
        </Flex>
      </div>
    );

  return <OuterLockContext.Provider value>{content}</OuterLockContext.Provider>;
}
